'use strict';

import axios from 'axios';
import { parseResponse, handleError } from '@/_helpers/utils.js';
import { ADMIN_ROLES, ROLE_COMPANY_ADMIN } from '@/_helpers/constants.js';

const getNextLocalId = (function () {
  let identifier = 0;
  return () => {
    return identifier++;
  };
})();

const getInitialState = () => {
  return {
    loading: true,
    error: null,
    invites: [],
    invitesTmp: [],
    invitesValidStatus: [],
    activeInviteLocalId: null,
    lastInviteLocalId: null,
    successInvited: false,
    successInvites: [],
    repeatedInvites: [],
    insertedFromExcelIds: [],
    file_loaded: true,
    sending: false,
    responsesPopupEnabled: false,
    forceSend: false,
    sendingMethod: '',
    isInvitesValid: false,
    canSendSms: true,
    smsData: null
  };
};

const state = getInitialState();
const DUPLICATE_INVITATIONS_CODE = 'duplicate';

const actions = {
  async sendInvitesCheck({ state, commit, dispatch, getters }, params = {}) {
    await dispatch('validateInvites');
    await dispatch('check_duplicate_invites');

    if (state.repeatedInvites.length) return Promise.resolve();
    commit('setSendingMethod', 'sendInvites');
    commit('setResponsesPopupStatus', getters.canEnableResponsesPopup);
    commit('setSending', false);

    if (state.responsesPopupEnabled) return Promise.resolve();

    return await dispatch('sendInvites', {
      isShowSmsBalanceNotificate: params && params.isShowSmsBalanceNotificate
    });
  },

  async forceInvitesCheck({ state, commit, dispatch, getters }, params = {}) {
    await dispatch('validateInvites');

    if (!state.forceSend) {
      commit('setSendingMethod', 'forceInvite');
      commit('setResponsesPopupStatus', getters.canEnableResponsesPopup);
    }

    if (!state.responsesPopupEnabled) {
      commit('setSending', false);
      return await dispatch('forceInvite', {
        isShowSmsBalanceNotificate: params && params.isShowSmsBalanceNotificate
      });
    }

    return Promise.resolve();
  },

  async sendInvites({ commit, dispatch }, params = {}) {
    commit('setSending', true);

    try {
      return await dispatch('createNewInvites', {
        forceInvite: 0,
        isShowSmsBalanceNotificate: params && params.isShowSmsBalanceNotificate
      });
    } catch (e) {
      throw new Error('invites.errors.emptyList');
    }
  },

  async checkSms({ getters }) {
    const newInvites = getters.newInvites;
    return await axios.post(`/1.0/invites/calculate-sms`, { invites: newInvites });
  },

  async forceInvite({ commit, dispatch }, params = {}) {
    try {
      commit('setSending', true);
      return await dispatch('createNewInvites', {
        forceInvite: 1,
        isShowSmsBalanceNotificate: params && params.isShowSmsBalanceNotificate
      });
    } catch (e) {
      throw new Error('invites.errors.emptyList');
    } finally {
      commit('setForceSend', false);
      commit('setResponsesPopupStatus', false);
      commit('setSending', false);
    }
  },

  validateInvites({ state, commit, getters }) {
    commit('setSending', true);

    if (state.invites.filter(i => !!i.errors).length > 0 || state.isInvitesValid === false) {
      return axios
        .post('/1.0/invites/validate', { invites: getters.preparedInvites })
        .then(r => {
          if (r.data.meta.error !== null) {
            let errors = r.data.meta.error.details.invites;
            let data = [...errors, ...r.data.data];
            commit('clear');
            commit('importFromFileSuccess', data);
            commit('setSending', false);
            throw new Error('invites.list.notification.save.error');
          } else {
            commit('setInvitesValid', true);
            let data = r.data.data;
            commit('clear');
            commit('importFromFileSuccess', data);
          }
        })
        .catch(e => {
          commit('setSending', false);

          if (e.response) {
            throw new Error('common.error.networkError');
          }

          throw e;
        });
    }
  },

  clearActiveProcess({ dispatch }) {
    dispatch('process/setName', null, { root: true });
  },

  updateCandidate({ commit }, { localId, candidate, tags, edited, open }) {
    commit('updateCandidate', { localId, candidate, tags, edited, open });
  },

  updateVacancy({ commit }, { localId, vacancy, edited }) {
    commit('updateVacancy', { localId, vacancy, edited });
  },

  updateStatus({ commit }, { localId, valid }) {
    commit('updateStatus', { localId, valid });
  },

  check_duplicate_invites({ commit, dispatch, getters }) {
    commit('createNewInvites');
    const newInvites = getters.newInvites;

    if (newInvites.length === 0) {
      return new Error('invites.errors.emptyList');
    }

    return axios
      .post(`/1.0/invites/check-duplicate?force=0`, {
        invites: newInvites
      })
      .then(response => {
        const data = parseResponse(response).data;
        commit('set_repeated_invites', data);
      })
      .catch(error => {
        const err = handleError(error);
        if (err.code === DUPLICATE_INVITATIONS_CODE) {
          commit('set_repeated_invites', err.details.invites);
        } else {
          commit('createNewInvitesFailure', err);
          dispatch('global/setError', err, { root: true });
        }
      });
  },

  createNewInvites(
    { commit, dispatch, rootGetters, getters },
    { forceInvite, isShowSmsBalanceNotificate }
  ) {
    commit('createNewInvites');
    const newInvites = getters.newInvites;

    if (newInvites.length === 0) {
      throw new Error('invites.errors.emptyList');
    }

    if (isShowSmsBalanceNotificate) {
      return axios
        .post(`/1.0/invites/calculate-sms`, { invites: newInvites })
        .then(response => {
          const data = response.data.data[0];
          commit(
            'setSmsData',
            Object.assign({}, data, {
              invitesCount: newInvites.length
            })
          );
          return data;
        })
        .then(data => {
          const model = data;
          // const model = Object.assign({}, data, {
          //   can_send: false
          // });
          if (model.can_send) {
            commit('setCanSendSms', true);
            return invitesRequest();
          } else {
            commit('setCanSendSms', false);
          }
        });
    } else {
      invitesRequest();
    }

    function invitesRequest() {
      return rootGetters['situation/api/invitesAPI']
        .createNewInvites(newInvites, forceInvite)
        .then(data => commit('createNewInvitesSuccess', data))
        .then(() => dispatch('company_new/get_rates', null, { root: true }))
        .catch(err => {
          if (err.code === DUPLICATE_INVITATIONS_CODE) {
            commit('repeatedInvitesError', err);
          } else {
            commit('createNewInvitesFailure', err);
            dispatch('global/setError', err, { root: true });
          }
        });
    }
  },

  importFromFile({ commit, dispatch, rootGetters }, { file, vacancy }) {
    commit('importFromFile');
    commit('set_file_loaded', false);
    return rootGetters['situation/api/invitesAPI']
      .importFromFile(file)
      .then(data => {
        if (vacancy) {
          data = data.map(candidate => {
            candidate.vacancy = vacancy;
            return candidate;
          });
        }
        commit('clear');
        commit('set_file_loaded', true);
        commit('importFromFileSuccess', data);
      })
      .then(() => dispatch('company_new/get_rates', null, { root: true }))
      .catch(err => {
        commit('set_file_loaded', true);
        dispatch('global/setError', err, { root: true });
      });
  },

  clear({ commit }) {
    commit('clear');
    commit('createNewEmptyRow');
  },

  deleteRow({ commit }, localId) {
    commit('deleteRow', localId);
  },

  clearRow({ commit }, localId) {
    commit('deleteRow', localId);
    commit('createNewEmptyRow');
  },

  resetState({ commit }) {
    commit('resetState');
  },

  clearRepeatedInvites({ commit }) {
    commit('clearRepeatedInvites');
  }
};

const mutations = {
  setSendingMethod(state, method) {
    state.sendingMethod = method;
  },

  setInvitesValid(state, isValid) {
    this.isInvitesValid = isValid;
  },

  setCanSendSms(state, v) {
    state.canSendSms = v;
  },

  setForceSend(state, isActive) {
    state.forceSend = isActive;
  },

  setResponsesPopupStatus(state, isActive) {
    state.responsesPopupEnabled = isActive;
  },

  setSending(state, sending) {
    state.sending = sending;
  },

  set_file_loaded(state, v) {
    state.file_loaded = v;
  },

  setSmsData(state, v) {
    state.smsData = v;
  },

  clear(state) {
    state.invitesTmp = [...state.invites];
    state.invites = [];
    state.repeatedInvites = [];
    state.invitesValidStatus = [];
  },

  deleteRow(state, localId) {
    state.invites = state.invites.filter(v => v.localId !== localId);
    state.repeatedInvites = state.repeatedInvites.filter(v => v.localId !== localId);
    state.invitesValidStatus = state.invitesValidStatus.filter(v => v.localId !== localId);
  },

  createNewEmptyRow(state) {
    state.lastInviteLocalId = getNextLocalId();
    let candidate = {
      name: null,
      surname: null,
      gender: null,
      phone: null,
      email: null,
      countryMask: null
    };
    state.invites.push({
      localId: state.lastInviteLocalId,
      vacancy: {
        id: null,
        name: null
      },
      candidate,
      status: {
        id: null,
        name: null
      },
      tags: '',
      stage: {
        id: null,
        name: null
      },
      edited: true
    });
  },

  updateCandidate(state, { localId, candidate, tags, edited, open }) {
    const isInValidList = state.invitesValidStatus.some(obj => obj.localId === localId);

    if (!isInValidList) {
      state.invitesValidStatus.push({
        localId: state.lastInviteLocalId,
        valid: true,
        emptyRow: false
      });
    }

    state.invites = state.invites.map(invite => {
      if (invite.localId === localId) {
        invite.edited = !!edited; // edited;
        invite.tags = tags; // tags;
        if (open) {
          invite.edited = true;
        }
        return Object.assign({}, invite, {
          candidate: Object.assign(invite.candidate, candidate)
        });
      }
      return invite;
    });
  },

  updateVacancy(state, { localId, vacancy, edited }) {
    let last = state.invites[state.invites.length - 1].localId;
    state.invites.forEach(i => {
      if (i.localId !== last) {
        // i.edited = false;
      }
    });
    state.invites = state.invites.map(invite => {
      if (invite.localId === localId) {
        invite.edited = true; // edited;
        return Object.assign({}, invite, {
          vacancy: Object.assign(invite.vacancy, vacancy)
        });
      }
      return invite;
    });
  },

  updateInvites(state, invites) {
    state.invites = invites;
  },

  updateStatus(state, { localId, newData }) {
    const el = state.invitesValidStatus.find(el => el.localId === localId);
    Object.assign(el, newData);
  },

  setActive(state, id) {
    state.activeInviteLocalId = id;
  },

  createNewInvites(state) {
    state.loading = true;
  },

  createNewInvitesSuccess(state, data) {
    state.successInvited = true;
    state.successInvites = data;
    state.loading = false;
  },

  importFromFile(state) {
    state.loading = true;
  },
  setIvitesTmp(state) {
    state.invites = state.invitesTmp;
  },

  importFromFileSuccess(state, data) {
    data = data.map(candidate => {
      // console.log(candidate.tags);
      // console.log(typeof candidate.tags);
      const tags =
        typeof candidate.tags === 'string' || candidate.tags === undefined
          ? candidate.tags === undefined
            ? ''
            : candidate.tags
          : candidate.tags.join(', ');

      return {
        ...candidate,
        tags: tags,
        localId: getNextLocalId(),
        vacancy: candidate.vacancy || {
          id: null,
          name: null
        },
        status: candidate.status || {
          id: null,
          name: null
        },
        stage: candidate.stage || {
          id: null,
          name: null
        },
        edited: false
      };
    });
    state.loading = false;
    // Insert to the start of array another array
    const emptyRowIds = state.invitesValidStatus.map(s => {
      if (s.emptyRow) {
        return s.localId;
      }
    });
    state.invites = state.invites.filter(i => !emptyRowIds.includes(i.localId));
    state.invitesValidStatus = state.invitesValidStatus.filter(
      i => !emptyRowIds.includes(i.localId)
    );
    state.invites.push(...data);
    const newStatuses = data.map(item => ({
      localId: item.localId,
      valid: true,
      emptyRow: false
    }));
    const newStatusesIds = newStatuses.map(item => item.localId);
    state.invitesValidStatus.push(...newStatuses);
    state.insertedFromExcelIds.push(...newStatusesIds);
  },

  createNewInvitesFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  resetState(state) {
    Object.assign(state, getInitialState());
  },

  set_repeated_invites(state, data) {
    state.loading = false;
    if (data && data.length) {
      state.repeatedInvites = data.map(invite => {
        invite.localId = parseInt(invite.localId);
        const candidate = state.invites.find(c => c.localId === invite.localId);

        return candidate ? Object.assign(invite, { candidate: candidate.candidate }) : invite;
      });
    }
  },

  repeatedInvitesError(state, err) {
    state.loading = false;
    state.repeatedInvites = err.details.invites.map(invite => {
      invite.localId = parseInt(invite.localId);
      const candidate = state.invites.find(c => c.localId === invite.localId);

      return candidate ? Object.assign(invite, { candidate: candidate.candidate }) : invite;
    });
  },

  clearRepeatedInvites(state) {
    state.repeatedInvites = [];
  }
};

const getters = {
  newInvites() {
    const filteredStatuses = state.invitesValidStatus.map(s => s.localId);
    const length = state.invitesValidStatus.length - 1;
    if (
      state.invitesValidStatus[length] &&
      state.invitesValidStatus[length].candidate &&
      state.invitesValidStatus[length].candidate.email === null &&
      state.invitesValidStatus[length].localId
    ) {
      filteredStatuses.splice(filteredStatuses.length - 1, 1);
    }

    return state.invites
      .filter(s => filteredStatuses.includes(s.localId))
      .filter((status, index) => {
        const length = state.invites.length - 1;
        if (index === length && status.candidate && status.candidate.email === null) {
          return false;
        } else {
          return true;
        }
      })
      .map(i => {
        const currentPhone = i.candidate.phone ? i.candidate.phone : '';
        if (
          i.candidate.countryMask !== undefined &&
          i.candidate.countryMask !== null &&
          i.candidate.phone !== null &&
          i.candidate.phone !== undefined &&
          currentPhone.indexOf('+') === -1
        ) {
          const candidate = Object.assign({}, i.candidate, {
            phone: '+' + i.candidate.countryMask + i.candidate.phone
          });

          return Object.assign({}, i, {
            candidate: candidate
          });
        } else {
          return i;
        }
      });
  },
  isInvitesValid(state) {
    return state.isInvitesValid;
  },
  canSendSms(state) {
    return state.canSendSms;
  },

  sendingMethod(state) {
    return state.sendingMethod;
  },

  forceSend(state) {
    return state.forceSend;
  },

  smsData(state) {
    return state.smsData;
  },

  is_admin(state, getters, rootState, rootGetters) {
    const currentUser = rootGetters['user/current'];

    if (!currentUser || !currentUser.roles || !currentUser.roles.length) {
      return false;
    }

    return [...ADMIN_ROLES, ROLE_COMPANY_ADMIN].some(adminRole =>
      currentUser.roles.some(({ role }) => role === adminRole)
    );
  },

  responsesPopupEnabled(state) {
    return state.responsesPopupEnabled;
  },

  canEnableResponsesPopup(state, getters, rootGetters) {
    const responseBalance = rootGetters['company_new/response_balance'];

    if (!getters.is_admin || !state.invites || !state.invites.length || responseBalance === null) {
      return false;
    }

    return responseBalance < state.invites.length;
  },
  preparedInvites(state) {
    let invites = [];
    let temp = state.invites.slice(0, state.invites.length - 1);
    temp.forEach(i => {
      let phone = '';
      if (i.candidate.phone && !!i.candidate.countryMask && i.candidate.phone[0] !== '+') {
        phone = '+' + i.candidate.countryMask + i.candidate.phone;
      } else {
        phone = i.candidate.phone;
      }
      let candidate = {
        name: i.candidate.name,
        surname: i.candidate.surname,
        email: i.candidate.email,
        phone
      };
      if (i.candidate.is_internal) {
        candidate.is_internal = i.candidate.is_internal;
      }
      const vacancy = i.vacancy;
      invites.push({ candidate, vacancy, tags: i.tags });
    });

    return invites;
  },

  sending(state) {
    return state.sending;
  },

  error(state) {
    return state.error;
  },

  loading(state) {
    return state.loading;
  },

  list: state => {
    return state.invites;
  },

  activeInviteLocalId: state => {
    return state.activeInviteLocalId;
  },

  invitesValidStatus: state => {
    return state.invitesValidStatus;
  },

  successInvited: state => {
    return state.successInvited;
  },

  insertedFromExcelIds: state => {
    return state.insertedFromExcelIds;
  },

  successCreatedInvites: state => {
    return state.successInvites;
  },

  repeatedInvites: state => {
    return state.repeatedInvites;
  },

  hasProcess: (state, getters, rootState, rootGetters) => process => {
    return rootGetters['dictionary/vacancies'].some(v => v.meta?.active_process === process);
  },

  file_loaded(state) {
    return state.file_loaded;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
